'use client';

import type { CSSProperties, ReactNode } from 'react';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { SwiperOptions } from 'swiper/types/swiper-options';

import { ArrowLeftSmall, ArrowRightSmall } from '@zoomit/icons';
import type { SpacingType } from '@zoomit/primitives';
import { Box } from '@zoomit/primitives';
import { theme } from '@zoomit/zoomit-ds';

type Props = {
  slides: ReactNode[];
  containerStyle?: CSSProperties;
  slideStyle?: CSSProperties;
  spacing?: SpacingType;
  hasPrevArrow?: boolean;
  hasNextArrow?: boolean;
} & SwiperOptions;

const SwiperBase = (props: Props) => {
  const { slides, spacing, hasPrevArrow, hasNextArrow, containerStyle, slideStyle, ...rest } = props;

  return (
    <Box className={'relative'} spacing={spacing} style={containerStyle}>
      <SwipeStyle {...rest} $slideStyle={slideStyle}>
        {slides.map((slide, index) => (
          <SwiperSlide key={index} virtualIndex={index}>
            {slide}
          </SwiperSlide>
        ))}
        {hasNextArrow && (
          <ArrowNextWrapper className="swiper-button-next">
            <ArrowRightSmall />
          </ArrowNextWrapper>
        )}
        {hasPrevArrow && (
          <ArrowPrevWrapper className="swiper-button-prev">
            <ArrowLeftSmall />
          </ArrowPrevWrapper>
        )}
      </SwipeStyle>
    </Box>
  );
};

export default SwiperBase;

const SwipeStyle = styled(Swiper)<{ $slideStyle?: CSSProperties }>`
  position: initial;

  .swiper-slide {
    width: fit-content;
    ${(props) => props.$slideStyle && { ...props.$slideStyle }};
  }
`;

const ArrowPrevWrapper = styled.div`
  justify-content: center;
  align-items: center;
  width: 4.8rem;
  height: 100%;
  border-left: 1px var(${theme.borderPrimary}) solid;
  top: 2.2rem;
  right: -4.8rem !important;

  svg {
    width: auto;
    height: auto;
  }

  &:hover {
    svg {
      fill: #3a3838;
    }
  }

  &::after {
    display: none;
  }
`;

const ArrowNextWrapper = styled.div`
  justify-content: center;
  align-items: center;
  width: 4.8rem;
  height: 100%;
  border-right: 1px var(${theme.borderPrimary}) solid;
  top: 2.2rem;
  left: -4.8rem !important;

  svg {
    width: auto;
    height: auto;
  }

  &:hover {
    svg {
      fill: #3a3838;
    }
  }

  &::after {
    display: none;
  }
`;
